import { useState } from "react";
import { useQuery } from "@tanstack/react-query";
import {
  adminFetchUsage,
  adminListApiKeys,
  listUsers,
} from "../lib/api";
import { useAuth } from "../lib/auth";
import {
  DateRangePicker,
  defaultRange,
  rangeToParams,
  type DateRange,
  type Granularity,
} from "../components/DateRangePicker";
import { UsageChart } from "../components/UsageChart";
import { UsageTable } from "../components/UsageTable";
import {
  Card,
  CardContent,
  CardHeader,
  CardTitle,
} from "../components/ui/card";
import { Label } from "../components/ui/label";

export function AdminUsageRoute() {
  const { me } = useAuth();
  const [range, setRange] = useState<DateRange>(() => defaultRange(30));
  const [granularity, setGranularity] = useState<Granularity>("day");
  const [userId, setUserId] = useState<string>("");
  const [apiKeyId, setApiKeyId] = useState<string>("");

  const users = useQuery({
    queryKey: ["admin", "users", "all-for-filter"],
    queryFn: () => listUsers(),
    staleTime: 60_000,
  });

  const keys = useQuery({
    queryKey: ["admin", "keys", "all-for-filter", userId],
    queryFn: () =>
      adminListApiKeys({ user_id: userId || undefined, only_active: false }),
    enabled: true,
  });

  const usage = useQuery({
    queryKey: ["admin", "usage", range, granularity, userId, apiKeyId],
    queryFn: () =>
      adminFetchUsage({
        ...rangeToParams(range),
        granularity,
        user_id: userId || undefined,
        api_key_id: apiKeyId || undefined,
      }),
  });

  if (!me?.is_admin) {
    return (
      <div className="rounded-md border bg-card p-6 text-sm">
        Kein Admin-Zugriff.
      </div>
    );
  }

  return (
    <Card>
      <CardHeader>
        <CardTitle>Globaler Verbrauch</CardTitle>
      </CardHeader>
      <CardContent className="space-y-6">
        <DateRangePicker
          value={range}
          onChange={setRange}
          granularity={granularity}
          onGranularityChange={setGranularity}
        />
        <div className="flex flex-wrap items-end gap-3">
          <div className="space-y-1">
            <Label htmlFor="user-filter">User</Label>
            <select
              id="user-filter"
              value={userId}
              onChange={(e) => {
                setUserId(e.target.value);
                setApiKeyId("");
              }}
              className="h-9 rounded-md border border-input bg-background px-2 text-sm"
            >
              <option value="">Alle</option>
              {users.data?.map((u) => (
                <option key={u.id} value={u.id}>
                  {u.email}
                </option>
              ))}
            </select>
          </div>
          <div className="space-y-1">
            <Label htmlFor="key-filter">API-Key</Label>
            <select
              id="key-filter"
              value={apiKeyId}
              onChange={(e) => setApiKeyId(e.target.value)}
              className="h-9 rounded-md border border-input bg-background px-2 text-sm"
            >
              <option value="">Alle</option>
              {keys.data?.map((k) => (
                <option key={k.id} value={k.id}>
                  {k.label} ({k.prefix}…)
                </option>
              ))}
            </select>
          </div>
        </div>
        {usage.isLoading && <p className="text-sm">Lade…</p>}
        {usage.isError && (
          <p className="text-sm text-destructive">
            Konnte Verbrauch nicht laden.
          </p>
        )}
        {usage.data && (
          <>
            <UsageChart
              buckets={usage.data.buckets}
              granularity={granularity}
            />
            <UsageTable
              buckets={usage.data.buckets}
              granularity={granularity}
            />
          </>
        )}
      </CardContent>
    </Card>
  );
}
